import { Grid, useTheme, useMediaQuery } from '@mui/material';

import {
  MainContainer,
  MainHeading,
  ParagraphText,
  SectionHeading,
  SubsectionHeading,
  HobbyGrid,
  HobbyIcon,
  HobbyTooltip,
  TooltipContent,
  SpotifyGrid,
  SpotifyIframe,
  StyledImageList,
  StyledImageListItem,
  StyledImageListItemBar,
} from './GetToKnowMe.styles';

import { formatDistanceToNow } from 'date-fns';
import uniqueId from 'lodash.uniqueid';

function GetToKnowMe() {
  const theme = useTheme();
  const large = useMediaQuery(theme.breakpoints.up('lg'));
  const medium = useMediaQuery(theme.breakpoints.up('md'));
  const small = useMediaQuery(theme.breakpoints.up('sm'));
  const tiny = useMediaQuery(theme.breakpoints.up('xs'));

  const itemData = [
    {
      img: 'kibethAndBeanFall.jpeg',
      title: 'Kibeth & Bean enjoy the fall',
    },
    {
      img: 'beanOnBlanket.jpeg',
      title: 'Beanie, my miniature Dachshund',
    },
    {
      img: 'chickies.jpeg',
      title: 'Our chickens first time out in the grass',
    },
    {
      img: 'laurelTrailWithPups.jpeg',
      title: 'The Laurel Sanctuary with my dogs',
    },
    {
      img: 'brandonAndI.jpeg',
      title: 'My husband Brandon and I',
    },
    {
      img: 'beanieBurrito.jpeg',
      title: 'A Beanie burrito',
    },
    {
      img: 'profile-picture.jpg',
      title: 'Hadley Swello',
    },
    {
      img: 'lemonCakes.jpeg',
      title: 'Fresh baked lemon cakes',
    },
    {
      img: 'homemadePasta.jpeg',
      title: 'Making fresh pasta',
    },
    {
      img: 'applePicking.jpeg',
      title: 'Apple picking as a family',
    },
    {
      img: 'backyard.jpeg',
      title: 'A cozy fall fire',
    },
    {
      img: 'tubing.jpeg',
      title: 'A tubing trip in New Hampshire',
    },
    {
      img: 'chocolateCake.jpeg',
      title: 'Fresh baked chocolate cake',
    },
    {
      img: 'kibeth.jpeg',
      title: 'Kibeth, my Husky mix',
    },
    {
      img: 'foccacia.jpeg',
      title: 'A gardenscape focaccia',
    },
  ];

  const responsiveColumnNumber = () => {
    if (large) {
      return 4;
    } else if (medium) {
      return 3;
    } else if (small) {
      return 2;
    } else if (tiny) {
      return 1;
    }
    return 4;
  };

  const hobbyIcons = [
    {
      title: 'Baking',
      src: 'bread-icon.png',
      ariaLabel: 'Bread and rolling pin',
    },
    {
      title: 'Cooking',
      src: 'cooking-icon.png',
      ariaLabel: 'Grocery basket with food',
    },
    {
      title: 'Dungeons & Dragons',
      src: 'dnd-icon.png',
      ariaLabel: 'Funny monster face',
    },
    {
      title: 'Embroidery',
      src: 'embroidery-icon.png',
      ariaLabel: 'Cross stitch hoop and thread',
    },
    {
      title: 'Foraging',
      src: 'foraging-icon.png',
      ariaLabel: 'Wild mushroom',
    },
    {
      title: 'Gardening',
      src: 'gardening-icon.png',
      ariaLabel: 'Greenhouse with plants',
    },
    {
      title: 'Hiking',
      src: 'hike-icon.png',
      ariaLabel: 'Hiking person with hiking stick',
    },
    {
      title: 'Knitting',
      src: 'knitting-icon.png',
      ariaLabel: 'Yarn and knitting needles',
    },
    {
      title: 'Music',
      src: 'music-icon.png',
      ariaLabel: 'Music note in red square',
    },
    {
      title: 'Reading',
      src: 'reading-icon.png',
      ariaLabel: 'Simple closed book',
    },
    {
      title: 'Video Games',
      src: 'video-games-icon.png',
      ariaLabel: 'Video game controller',
    },
    {
      title: 'Watercolor Painting',
      src: 'art-icon.png',
      ariaLabel: 'Paintbrush and pencil',
    },
  ];

  return (
    <MainContainer>
      <MainHeading variant="h2" component="h2">
        Hello World!
      </MainHeading>
      <ParagraphText paragraph>
        With {formatDistanceToNow(new Date('2017/07/01'))} as a software
        engineer, I&apos;ve found a lovely balance between creativity and
        code—crafting solutions that are functional, reliable, and genuinely
        pleasant to maintain for whoever comes next. My path to tech began in
        the classroom as a fourth-generation teacher, where I learned that
        patience and clear communication are just as vital as any technical
        skill.
      </ParagraphText>
      <ParagraphText paragraph>
        A year abroad in Belgium as a Rotary exchange student, family ties to
        Brazil, and a childhood filled with travel gave me a deep appreciation
        for connecting across cultures. I&apos;m conversational in French and
        can follow along in Spanish and Portuguese. That same curiosity carries
        into my work: whether I&apos;m deep in React, navigating the intricacies
        of Ruby on Rails, or picking up something entirely new, I find genuine
        joy in solving problems and building things that make someone&apos;s day
        a little easier.
      </ParagraphText>
      <ParagraphText paragraph>
        Beyond the screen, you&apos;ll find me mentoring LGBTQ+ youth in tech,
        helping to build a community where everyone feels welcome to pursue
        their dreams. I try to approach my work—and life—through the lens of
        Rotary International&apos;s Four-Way Test: Is it the truth? Is it fair
        to all concerned? Will it build goodwill and better friendships? Will it
        be beneficial to all concerned? It&apos;s a simple framework, but one
        that keeps me grounded.
      </ParagraphText>
      <SectionHeading paragraph variant="h3">
        Hobbies & Interests
      </SectionHeading>
      <HobbyGrid container spacing={1}>
        {hobbyIcons.map(hobby => {
          return (
            <Grid item sm={1} md={1} lg={1} key={uniqueId(hobby.title)}>
              <HobbyTooltip
                arrow
                title={
                  <TooltipContent variant="h4">{hobby.title}</TooltipContent>
                }
              >
                <HobbyIcon
                  alt={hobby.title}
                  src={hobby.src}
                  variant="square"
                  aria-label={hobby.ariaLabel}
                />
              </HobbyTooltip>
            </Grid>
          );
        })}
      </HobbyGrid>
      <SubsectionHeading paragraph variant="h4">
        Listen along with me...
      </SubsectionHeading>
      <SpotifyGrid container spacing={1}>
        <Grid item xs={12} sm={12} md={12} lg={4}>
          <SpotifyIframe
            src="https://open.spotify.com/embed/playlist/37i9dQZF1E8Lem4INTaSCj?utm_source=generator"
            allowFullScreen=""
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
            title="Redwood Tree Playlist"
            aria-label="Redwood Tree Playlist - embedded music player"
          ></SpotifyIframe>
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={4}>
          <SpotifyIframe
            src="https://open.spotify.com/embed/playlist/37i9dQZF1EIfxw8oghwfcN?utm_source=generator"
            allowFullScreen=""
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
            title="Spotify playlist: Dinner Party"
            aria-label="Spotify playlist: Dinner Party - embedded music player"
          ></SpotifyIframe>
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={4}>
          <SpotifyIframe
            src="https://open.spotify.com/embed/playlist/37i9dQZF1EIg08g8RGinF1?utm_source=generator"
            allowFullScreen=""
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
            title="Spotify playlist: Garage Punk"
            aria-label="Spotify playlist: Garage Punk - embedded music player"
          ></SpotifyIframe>
        </Grid>
      </SpotifyGrid>
      <SubsectionHeading paragraph variant="h4">
        A window into my life...
      </SubsectionHeading>
      <StyledImageList
        variant="masonry"
        cols={responsiveColumnNumber()}
        gap={8}
      >
        {itemData.map(item => (
          <StyledImageListItem key={item.img}>
            <img
              srcSet={`${item.img}?w=248&fit=crop&auto=format&dpr=2 2x`}
              src={`${item.img}?w=248&fit=crop&auto=format`}
              alt=""
              loading="lazy"
            />
            <StyledImageListItemBar title={item.title} />
          </StyledImageListItem>
        ))}
      </StyledImageList>
    </MainContainer>
  );
}

export default GetToKnowMe;
